import { FC, useCallback, useMemo, ReactNode } from 'react'
import { createImageTokenRenderer, createVideoInPopupTokenRenderer } from 'helpers/insightMarkdownHelper'
import { captureWarning } from 'helpers/errorHelpers'
import { createRenderTokensToJsx, parseMarkdown } from 'helpers/markdownHelpers'
import { ImageMarkdownNode, MarkdownToken } from 'marked-ast'

type MarkdownTextProps = {
  text: string
}

const cleanUpTokens = (tokens: MarkdownToken[]): MarkdownToken[] => {
  return tokens.reduce<MarkdownToken[]>((acc, token) => {
    if (typeof token === 'string') {
      return acc
    }

    if (token.type === 'paragraph') {
      return [
        ...acc,
        {
          ...token,
          text: cleanUpTokens(token.text),
        },
      ]
    }

    if (token.type === 'video' || token.type === 'image') {
      return [
        ...acc,
        token,
      ]
    }

    return acc
  }, [])
}

export const MarkdownMedia: FC<MarkdownTextProps> = (props) => {
  const { text } = props

  const handleError = useCallback((tokenType) => {
    captureWarning(`Invalid insight text`, { text, property: 'text', tokenType })
  }, [text])
  const imageRenderer = useMemo(() => createImageTokenRenderer(handleError), [handleError])
  const videoRenderer = useMemo(() => createVideoInPopupTokenRenderer(handleError), [handleError])

  const handleParagraph = useCallback<(token: ImageMarkdownNode, key: string) => ReactNode>((token): ReactNode[] => {
    return renderTokensToJsx(token.text)
  }, [videoRenderer])

  const renderTokensToJsx = useMemo(() => createRenderTokensToJsx({
    image: imageRenderer,
    video: videoRenderer,
    paragraph: handleParagraph,
  }), [imageRenderer, videoRenderer, handleParagraph])

  const markdownTokens = useMemo(() => {
    const tokens = parseMarkdown(text)
    return cleanUpTokens(tokens)
  }, [text])

  const contentChild = useMemo(() => {
    return renderTokensToJsx(markdownTokens)
  }, [markdownTokens, renderTokensToJsx])

  return (
    <>
      { contentChild }
    </>
  )
}
